import { format, set } from 'date-fns';
import { createSelector } from 'reselect';

import { getDateLabel } from 'components/statistics/sales-after-sales/common/utils';

import { EXTENSIONS_ENUM } from 'constants/ExtensionConstants';
import { GOLD, PLATINUM } from 'constants/Global';
import { ACCEPTED, ANSWERED, IN_PROGRESS, SENT, TO_BE_SENT } from 'constants/InterventionConstants';
import { MODE_TO_EXTENSION } from 'constants/ModeConstants';
import {
  SELF_SERVICE_STATS_ORIGIN,
  SELF_SERVICE_STATS_REQUEST_TYPES,
  SELF_SERVICE_STATS_STATUS_BY_ORIGIN,
  SELF_SERVICE_STATS_HOUR_DISTRIBUTION_REDUCER,
  SELF_SERVICE_STATS_TIME_TO_COMPLETE_BY_ORIGIN
} from 'constants/SelfServiceConstants';
import {
  DAY,
  PROVIDED,
  REFUSED,
  REQUEST_DATE_FORMATTER,
  SELF_SERVICE_EXTRAS,
  SELF_SERVICE_QUESTIONS,
  SELF_SERVICE_STATUS,
  SELF_SERVICE_TOP_CONTRIBUTORS,
  CONFIRMED,
  CANCELED,
  SELF_SERVICE_PROACTIVE_WORKS,
  TO_VALIDATE,
  AUTOMATIC_VALIDATION_PROVIDED,
  AUTOMATIC_AUTOMATICALLY_ACCEPTED,
  AUTOMATIC_MANUALLY_ACCEPTED,
  MANUALLY_REFUSED
} from 'constants/StatisticsConstants';
import { DEALER_STATUS } from 'constants/StatusConstants';

import { getBrandByIds } from 'modules/brands/selectors';
import { getLocale } from 'modules/i18n/selectors/data';
import { isSelfServiceStats, isSelfServiceStatsDetails } from 'modules/router/selectors';
import {
  getMode,
  getPeriod,
  getModules,
  getBaseRequestParams,
  getDealersStatsFilters,
  getDetailsStatsSelfServiceFilters,
  getDetailsStatsSelfServiceGroupBy
} from 'modules/statistics/selectors/ui';
import { getCurrentUserIsAdmin, getCurrentUserIsManufacturer } from 'modules/users/selectors/currentUser';
import { getHideTopContributorsTab, getSelectedDealerId, getSelectedDealerGroupId } from 'selectors/dealerSelectors';

import { getFiltersRequest, getSelectedQuestionModelId } from './selfService/questions';
import { CHAT_CHART_LEGEND_NAME } from './utils';

/**
 * @param {{ dealerStatistics : import('modules/statistics/reducer').StatisticsReducer}} state
 * @returns {import('modules/statistics/reducer').StatisticsReducer}
 */
const getState = state => state.dealerStatistics;

const getData = createSelector(getState, state => state.data);
const getRoot = state => state;
const getProps = (_, props) => props;

const getAnswerTimeReducer = createSelector(getData, data => data.answerTime);
export const getAnswerTime = createSelector(getAnswerTimeReducer, reducer => reducer.data);
export const getAnswerTimeApiError = createSelector(getAnswerTimeReducer, reducer => reducer.apiError);
export const getAnswerTimeIsFetching = createSelector(getAnswerTimeReducer, reducer => reducer.isFetching);

const getBrandsReducer = createSelector(getData, data => data.brands);
const getBrandStatsIds = createSelector(getBrandsReducer, reducer => reducer.data);
export const getBrandStats = createSelector(getRoot, getBrandStatsIds, getBrandByIds);
export const getBrandStatsIsFetching = createSelector(getBrandsReducer, reducer => reducer.isFetching);

const getCustomerOpinionReducer = createSelector(getData, data => data.customerOpinionStats);
const getCustomerOpinionStats = createSelector(getCustomerOpinionReducer, state => state.data);
const getSelfServiceTopContributorsReducer = createSelector(getData, data => data.selfServiceTopContributorsStats);
export const getSelfServiceTopContributorsStats = createSelector(getSelfServiceTopContributorsReducer, ({ data }) => data.items);
export const getSelfServiceTopContributorsStatsPage = createSelector(getSelfServiceTopContributorsReducer, ({ data }) => data.page ?? {});
export const getSelfServiceTopContributorsStatsApiError = createSelector(getSelfServiceTopContributorsReducer, ({ apiError }) => apiError);
export const getSelfServiceTopContributorsStatsIsFetching = createSelector(getSelfServiceTopContributorsReducer, ({ isFetching }) => isFetching);
export const getSelfServiceTopContributorsFilters = createSelector(getSelfServiceTopContributorsReducer, ({ filters }) => filters);
export const getCustomerOpinionStatsApiError = createSelector(getCustomerOpinionReducer, state => state.apiError);
export const getCustomerOpinionStatsIsFetching = createSelector(getCustomerOpinionReducer, state => state.isFetching);
export const getCustomerOpinion = createSelector(getCustomerOpinionStats, data =>
  data
    .filter(({ opinion }) => ['CALL_ME_BACK', 'YES', 'NO'].includes(opinion))
    .map(({ opinion, count: value }) => ({
      name: opinion.toLowerCase(),
      value
    })));

const dealersStatsReducer = createSelector(getData, state => state.dealersStats);
export const getDealersStats = createSelector(dealersStatsReducer, state => state.data.items);
export const getDealersStatsPage = createSelector(dealersStatsReducer, state => state.data.page);
export const getDealersStatsIsFetching = createSelector(dealersStatsReducer, state => state.isFetching);
export const getDealersStatsApiError = createSelector(dealersStatsReducer, state => state.apiError);

const getTotal = createSelector(getData, state => state.dealersStatsTotal);

const getCountries = createSelector(getTotal, total => total.countries);

const getTotalCountry = createSelector(getCountries, countries =>
  (countries.length === 1 ? countries[0].country : undefined));

export const getTotalData = createSelector(getTotal, getTotalCountry, (total, country) => ({
  ...total.data,
  dealerId: 'all',
  dealerName: 'all',
  country
}));

const getInterventionCountReducer = createSelector(getData, data => data.interventionCount);
export const getInterventionCount = createSelector(getInterventionCountReducer, reducer => reducer.data);
export const getInterventionCountIsFetching = createSelector(getInterventionCountReducer, data => data.isFetching);
export const getInterventionCountApiError = createSelector(getInterventionCountReducer, data => data.apiError);

const getInterventionRatingReducer = createSelector(getData, data => data.interventionRating);
const getInterventionRatingStats = createSelector(getInterventionRatingReducer, state => state.data);
const getInterventionRatingStatsApiError = createSelector(getInterventionRatingReducer, state => state.apiError);
const getInterventionRatingStatsIsFetching = createSelector(
  getInterventionRatingReducer,
  state => state.isFetching
);

const getRate = (data, nbStars) => data?.find(({ rating }) => rating === nbStars)?.id ?? 0;
export const getInterventionRating = createSelector(getInterventionRatingStats, data => {
  let total = 0;
  const rating = [];

  for (let i = 0; i < 5; i++) {
    const rate = getRate(data, i + 1);
    rating.push(rate);
    total += rate;
  }

  return {
    rating: total > 0 ? rating : [],
    total,
    average: rating.reduce((acc, value, index) => acc + value * (index + 1), 0) / (total || 1)
  };
});

const getFollowUpReducer = createSelector(getData, data => data.followUpStats);
export const getFollowUpStats = createSelector(getFollowUpReducer, ({ data = {} }) => ({
  sentFollowUp: data.hasFollowUpSent,
  sentFollowUpRatio: data.sent > 0 ? data.hasFollowUpSent / data.sent : 0,
  ratingComments: data.hasRatingComment
}));
const isLoadingFollowUpStats = createSelector(getFollowUpReducer, ({ loading }) => loading);
const hasErrorFollowUpStats = createSelector(getFollowUpReducer, ({ hasError }) => hasError);

export const isLoadingRating = createSelector(
  getInterventionRatingStatsIsFetching,
  isLoadingFollowUpStats,
  (loadingRating, loadingFollowUp) => loadingRating || loadingFollowUp
);
export const hasRatingError = createSelector(
  getInterventionRatingStatsApiError,
  hasErrorFollowUpStats,
  (ratingError, followUpError) => ratingError || followUpError
);

const getInterventionStatsReducer = createSelector(getData, data => data.interventionStats);
const getStatusInterventionStats = createSelector(getInterventionStatsReducer, reducer => reducer.data);
export const getStatusInterventionStatsApiError = createSelector(getInterventionStatsReducer, data => data.apiError);
export const getStatusInterventionStatsIsFetching = createSelector(
  getInterventionStatsReducer,
  data => data.isFetching
);
export const getStatusInterventionInnerPie = createSelector(
  getStatusInterventionStats,
  ({ answered = 0, read = 0, sent = 0, notSent = 0 }) => [
    { name: 'answered', value: answered },
    { name: 'readOn', value: read },
    { name: 'sentOn', value: sent },
    { name: 'notSent', value: notSent }
  ]
);
export const getStatusInterventionOuterPie = createSelector(
  getStatusInterventionStats,
  ({ accepted = 0, rejected = 0 }) => [
    { name: 'acceptedOn', value: accepted, hasSeparator: true },
    { name: 'rejectedOn', value: rejected }
  ]
);

const getRevenueByBrandReducer = createSelector(getData, data => data.revenueByBrandStats);
export const getRevenueByBrandStats = createSelector(getRevenueByBrandReducer, reducer => reducer.data);
export const getRevenueByBrandStatsIsFetching = createSelector(getRevenueByBrandReducer, data => data.isFetching);
export const getRevenueByBrandStatsApiError = createSelector(getRevenueByBrandReducer, data => data.apiError);

const getRevenueByPeriodStatsReducer = createSelector(getData, data => data.revenueByPeriodStats);
export const getRevenueByPeriodStats = createSelector(getRevenueByPeriodStatsReducer, root => root.data);
export const getRevenueByPeriodStatsIsFetching = createSelector(
  getRevenueByPeriodStatsReducer,
  root => root.isFetching
);
export const getRevenueByPeriodStatsApiError = createSelector(getRevenueByPeriodStatsReducer, root => root.apiError);

const getRevenueStateReducer = createSelector(getData, data => data.revenueStats);
export const getRevenueStats = createSelector(getRevenueStateReducer, reducer => reducer.data);
export const getRevenueStatsApiError = createSelector(getRevenueStateReducer, reducer => reducer.apiError);
export const getRevenueStatsIsFetching = createSelector(getRevenueStateReducer, reducer => reducer.isFetching);
export const getRevenuePieStats = createSelector(getRevenueStats, ({ acceptedAmount = 0, rejectedAmount = 0 }) => [
  {
    name: 'acceptedOn',
    value: acceptedAmount
  },
  {
    name: 'rejectedOn',
    value: rejectedAmount
  }
]);

const getStatusTimeLineStateReducer = createSelector(getData, data => data.timeLineStats);
export const getStatusTimeLineStats = createSelector(getStatusTimeLineStateReducer, data => data.data);
export const getStatusTimeLineStatsApiError = createSelector(getStatusTimeLineStateReducer, data => data.apiError);
export const getStatusTimeLineStatsIsFetching = createSelector(getStatusTimeLineStateReducer, data => data.isFetching);

const getTopContributorsReducer = createSelector(getData, state => state.topContributorsStats);
export const getTopContributorsStats = createSelector(getTopContributorsReducer, ({ data }) => data.items);
export const getTopContributorsStatsPage = createSelector(getTopContributorsReducer, ({ data }) => data.page ?? {});
export const getTopContributorsStatsApiError = createSelector(getTopContributorsReducer, ({ apiError }) => apiError);
export const getTopContributorsStatsIsFetching = createSelector(
  getTopContributorsReducer,
  ({ isFetching }) => isFetching
);

const getChatReducer = createSelector(getData, ({ chat }) => chat);
const getChatStats = createSelector(getChatReducer, ({ data }) => data);
export const getChatStatsApiError = createSelector(getChatReducer, ({ apiError }) => apiError);
export const getChatStatsIsFetching = createSelector(getChatReducer, ({ isFetching }) => isFetching);

export const getChannelsChatStats = createSelector(getChatStats, ({ channels }) => channels ?? []);

export const getNbCustomerMessageChatStats = createSelector(
  getChatStats,
  ({ nbCustomerMessages }) => nbCustomerMessages ?? 0
);
export const getNbSentMessageChatStats = createSelector(getChatStats, ({ nbSentMessages }) => nbSentMessages ?? 0);
export const getNbInternalMessageChatStats = createSelector(
  getChatStats,
  ({ nbInternalMessages }) => nbInternalMessages ?? 0
);

export const getAveragePerInterventionChatStats = createSelector(
  getChatStats,
  ({ averagePerIntervention }) => averagePerIntervention ?? 0
);
export const getAverageTimeToAnswerChatStats = createSelector(
  getChatStats,
  ({ averageTimeToAnswer }) => averageTimeToAnswer ?? 0
);

export const getNbMediaFilesSharedWithCustomers = createSelector(getChatStats, ({ nbMediaFilesSharedWithCustomers }) => nbMediaFilesSharedWithCustomers ?? 0);

export const getNbMediaFileSharedInternally = createSelector(getChatStats, ({ nbMediaFileSharedInternally }) => nbMediaFileSharedInternally ?? 0);

export const getAverageNbMediaFilesSharedInChatPerFile = createSelector(
  getChatStats,
  ({ averageNbMediaFilesSharedInChatPerFile }) => averageNbMediaFilesSharedInChatPerFile ?? 0
);

export const getNbMediaFilesSentToCustomers = createSelector(getChatStats, ({ nbMediaFilesSentToCustomers }) =>
  [
    { name: CHAT_CHART_LEGEND_NAME.PHOTOS, value: nbMediaFilesSentToCustomers?.serviceAdvisorMessagePhotosCount ?? 0 },
    { name: CHAT_CHART_LEGEND_NAME.VIDEOS, value: nbMediaFilesSentToCustomers?.serviceAdvisorMessageVideosCount ?? 0 },
    { name: CHAT_CHART_LEGEND_NAME.FILES, value: nbMediaFilesSentToCustomers?.serviceAdvisorMessageDocumentsCount ?? 0 }
  ]);

export const getNbMediaFilesReceivedToCustomers = createSelector(getChatStats, ({ nbMediaFilesReceivedToCustomers }) => [
  { name: CHAT_CHART_LEGEND_NAME.PHOTOS, value: nbMediaFilesReceivedToCustomers?.customerMessagePhotosCount ?? 0 },
  { name: CHAT_CHART_LEGEND_NAME.VIDEOS, value: nbMediaFilesReceivedToCustomers?.customerMessageVideosCount ?? 0 },
  { name: CHAT_CHART_LEGEND_NAME.FILES, value: nbMediaFilesReceivedToCustomers?.customerMessageDocumentsCount ?? 0 }
]);

export const getMessagesByInterventionChatStats = createSelector(
  getChatStats,
  getProps,
  ({ messagesByIntervention }, { intl, groupBy = DAY, dateField }) =>
    messagesByIntervention?.map(item => ({
      ...item,
      ...getDateLabel(item, groupBy, intl, dateField)
    })) ?? []
);

export const getMessagesDistributionChatStats = createSelector(
  getChatStats,
  ({ messagesDistribution }) => messagesDistribution ?? []
);

const getSelfServiceDetailsReducer = createSelector(getData, ({ selfServiceDetails }) => selfServiceDetails);
const getSelfServiceStatusReducer = createSelector(getSelfServiceDetailsReducer, ({ status }) => status);
const getSelfServiceQuestionsReducer = createSelector(getSelfServiceDetailsReducer, ({ questions }) => questions);
const getSelfServiceExtrasReducer = createSelector(getSelfServiceDetailsReducer, ({ extras }) => extras);
const getSelfServiceProactiveWorksReducer = createSelector(getSelfServiceDetailsReducer, ({ proactiveWorks }) => proactiveWorks);

export const getSelfServiceTab = createSelector(
  getSelfServiceDetailsReducer,
  getHideTopContributorsTab,
  getCurrentUserIsManufacturer,
  ({ selectedTab }, hideTopContributorsTab, isManufacturer) => {
    if (selectedTab === SELF_SERVICE_TOP_CONTRIBUTORS && (hideTopContributorsTab || isManufacturer)) {
      return SELF_SERVICE_STATUS;
    }
    return selectedTab;
  }
);
export const getExtraDetailsData = createSelector(getSelfServiceExtrasReducer, ({ data }) => data);
export const getProactiveWorksDetailsData = createSelector(getSelfServiceProactiveWorksReducer, ({ data }) => data);

const getQuestionSelfServiceData = createSelector(getSelfServiceQuestionsReducer, ({ data }) => data);
export const getQuestionModelStatisticsCountValue = createSelector(
  getQuestionSelfServiceData,
  ({ count }) => count ?? 0
);
const getModal = createSelector(getQuestionSelfServiceData, ({ exportModal }) => exportModal);
export const isExportModalQuestionSelfServiceVisible = createSelector(getModal, ({ isVisible }) => isVisible);
export const isExportModalQuestionSelfServiceFetching = createSelector(getModal, ({ isFetching }) => isFetching);

const getSelfServiceDashboardReducer = createSelector(getData, ({ selfServiceDashboard }) => selfServiceDashboard);
export const getSelfServiceDashboardStats = createSelector(getSelfServiceDashboardReducer, ({ data }) => data);
const getSelfServiceDashboardStatsWithoutTotal = createSelector(getSelfServiceDashboardStats, data =>
  data.filter(({ dealerId }) => dealerId !== 'all'));
export const getSelfServiceDashboardStatsWTotal = createSelector(getSelfServiceDashboardStats, data =>
  data.find(({ dealerId }) => dealerId === 'all'));
export const getSelfServiceDashboardPage = createSelector(getSelfServiceDashboardReducer, ({ page }) => page);
export const getSelfServiceDashboardIsFetching = createSelector(
  getSelfServiceDashboardReducer,
  ({ isFetching }) => isFetching
);
export const getSelfServiceDashboardApiError = createSelector(
  getSelfServiceDashboardReducer,
  ({ apiError }) => apiError
);

const getAuditInFilesTableReducer = createSelector(getData, ({ auditInFilesTable }) => auditInFilesTable);
export const getAuditInFilesTableData = createSelector(getAuditInFilesTableReducer, ({ data }) => data);
export const getAuditInFilesTablePage = createSelector(getAuditInFilesTableReducer, ({ page }) => page);
export const getAuditInFilesTableSort = createSelector(getAuditInFilesTableReducer, ({ sort }) => sort);
export const getAuditInFilesTableIsFetching = createSelector(getAuditInFilesTableReducer, ({ isFetching }) => isFetching);
export const getAuditInFilesTableApiError = createSelector(getAuditInFilesTableReducer, ({ apiError }) => apiError);

const getAuditInFilesSummaryReducer = createSelector(getData, ({ auditInFilesSummary }) => auditInFilesSummary);
export const getAuditInFilesSummaryData = createSelector(getAuditInFilesSummaryReducer, ({ data }) => data);
export const getAuditInFilesSummaryIsFetching = createSelector(getAuditInFilesSummaryReducer, ({ isFetching }) => isFetching);
export const getAuditInFilesSummaryApiError = createSelector(getAuditInFilesSummaryReducer, ({ apiError }) => apiError);

export const getDealerSelectParamsForStats = createSelector(
  getMode,
  isSelfServiceStats,
  getCurrentUserIsAdmin,
  (mode, isSelfService, isAdmin) => {
    const extensions = isSelfService ? EXTENSIONS_ENUM.SELF_SERVICE : MODE_TO_EXTENSION[mode];
    return {
      extensions,
      demo: isAdmin ? undefined : false,
      status: [DEALER_STATUS.ACTIVE],
      fleetbackPacks: isSelfService ? undefined : [GOLD, PLATINUM]
    };
  }
);

export const getSelfServiceStatsDashboardFetchParams = createSelector(
  getPeriod,
  getSelectedDealerGroupId,
  getSelectedDealerId,
  getDealersStatsFilters,
  getCurrentUserIsAdmin,
  getProps,
  ({ from, to }, groupId, dealerId, { brandIds: brands, modules, dateField, countryCodes }, isAdmin, additionalParams = {}) => ({
    ...additionalParams,
    brands,
    modules,
    dateField,
    groupId: groupId || undefined,
    countryCodes,
    dealerId: dealerId || undefined, // handle 'all dealers' in select
    ...(REQUEST_DATE_FORMATTER[dateField]?.transform?.(from, to) ?? REQUEST_DATE_FORMATTER.DEFAULT.transform(from, to)),
    exist: REQUEST_DATE_FORMATTER[dateField]?.exist,
    demo: isAdmin ? undefined : false
  })
);

export const getSelfServiceStatsDetailsFetchParams = createSelector(
  getSelfServiceStatsDashboardFetchParams,
  getDetailsStatsSelfServiceFilters,
  getDetailsStatsSelfServiceGroupBy,
  (dashboardFilters, { type, ...filters }, groupBy) => ({
    ...dashboardFilters,
    types: type,
    ...filters,
    groupBy,
  })
);

const getSelfServiceDetailsDataReducer = createSelector(getSelfServiceStatusReducer, ({ data }) => data);
export const getSelfServiceDetailsStatsApiError = createSelector(
  getSelfServiceStatusReducer,
  ({ apiError }) => apiError
);
export const getSelfServiceDetailsStats = createSelector(
  getSelfServiceDetailsDataReducer,
  ({ [SELF_SERVICE_STATS_REQUEST_TYPES.DETAILED_STATS]: data }) =>
    Object.entries(data[0] ?? {}).reduce(
      (acc, [key, value]) => ({
        ...acc,
        ...(Object.values(SELF_SERVICE_STATS_TIME_TO_COMPLETE_BY_ORIGIN).includes(key)
          ? { timeToComplete: value }
          : { [key]: value })
      }),
      {}
    )
);
export const getSelfServiceDetailsStatsCountByPeriod = createSelector(
  getSelfServiceDetailsDataReducer,
  ({ [SELF_SERVICE_STATS_REQUEST_TYPES.COUNT_BY_PERIOD]: data }) => data
);

const STATUS_LIST_BY_ORIGIN = {
  [SELF_SERVICE_STATS_ORIGIN.HOME]: [ANSWERED, IN_PROGRESS, SENT],
  [SELF_SERVICE_STATS_ORIGIN.ON_SITE_OR_KIOSK]: [ANSWERED, IN_PROGRESS]
};

const getExistingKey = (obj, keys) => keys.find(key => key in obj);

const countByStatusFormatter = (data, origin, type) => {
  const statuses = STATUS_LIST_BY_ORIGIN[origin];
  const fields = SELF_SERVICE_STATS_STATUS_BY_ORIGIN[origin][type];

  const emptyDetails = fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), { total: 0 });
  let initialValue = statuses.reduce((acc, status) => ({ ...acc, [status]: { total: 0, details: [] } }), {});

  if (fields.includes('manualCheckOutCompleted')) {
    initialValue = {
      manualCheckOutCompleted: {
        total: data.find(({ manualCheckOutCompleted }) => manualCheckOutCompleted)?.id || 0,
        details: {}
      },
      ...initialValue
    };
  }

  const results = statuses.reduce((acc, status, index) => {
    const totalWorkflow = acc[statuses[index - 1]]?.total ?? 0;

    const subData = data.filter(row => {
      const field = getExistingKey(row, fields);
      const { [field]: rowStatus } = row;
      return rowStatus === status;
    });

    const summary = subData.reduce(
      (acc2, row) => {
        const { dropOffStep: step, id: total = 0 } = row;
        if (typeof step !== 'undefined') {
          const rowStatus = getExistingKey(row, fields);

          return {
            total: acc2.total + total,
            details: {
              ...acc2.details,
              [step]: {
                ...(acc2.details?.[step] ?? emptyDetails),
                total: acc2.details[step]?.total ?? 0 + total,
                [rowStatus]: acc2.details[step]?.[rowStatus] ?? 0 + total
              }
            }
          };
        }
        return { ...acc2, total: acc2.total + total };
      },
      { total: 0, details: [] }
    );

    return {
      ...acc,
      [status]: {
        ...summary,
        total: totalWorkflow + summary.total
      }
    };
  }, initialValue);

  // Special case for TO_BE_SENT status which take the highest count as total (there is a call to get the total that is always the highest count)
  const total = Math.max(...data.map(({ id }) => id));
  results[TO_BE_SENT] = {
    summary: { total, details: {} },
    total
  };

  return results;
};

export const getSelfServiceDetailsStatsCountByStatus = createSelector(
  getSelfServiceDetailsDataReducer,
  getDetailsStatsSelfServiceFilters,
  ({ [SELF_SERVICE_STATS_REQUEST_TYPES.COUNT_BY_STATUS]: data }, { origin, type }) =>
    countByStatusFormatter(data, origin, type)
);

export const getSelfServiceDetailsStatsHourDistribution = createSelector(
  getSelfServiceDetailsDataReducer,
  getDetailsStatsSelfServiceFilters,
  ({ [SELF_SERVICE_STATS_REQUEST_TYPES.HOUR_DISTRIBUTION]: data }, { origin }) =>
    [...Array(24).keys()].map(hour => {
      const date = set(new Date(), { hours: hour, minutes: 0, seconds: 0 });

      const id =
        data.find(
          ({ [SELF_SERVICE_STATS_HOUR_DISTRIBUTION_REDUCER[origin]]: dataHour }) =>
            format(date, 'HH:00:00') === dataHour
        )?.id ?? 0;

      return {
        [SELF_SERVICE_STATS_HOUR_DISTRIBUTION_REDUCER[origin]]: format(date, 'p'),
        id
      };
    })
);

export const isFetchingSelfServiceDetailsStatsCountByPeriod = createSelector(
  getSelfServiceStatusReducer,
  ({ isFetching }) => isFetching[SELF_SERVICE_STATS_REQUEST_TYPES.COUNT_BY_PERIOD]
);
export const isFetchingSelfServiceDetailsStats = createSelector(getSelfServiceStatusReducer, ({ isFetching }) =>
  Object.values(isFetching).some(fetching => fetching));

const hasSelfServiceDataToExportSelector = createSelector(
  isSelfServiceStatsDetails,
  getSelfServiceDashboardStatsWithoutTotal,
  getSelfServiceDetailsDataReducer,
  getExtraDetailsData,
  getProactiveWorksDetailsData,
  getSelfServiceTab,
  (
    isDetailsLocation,
    dashboardStats,
    { [SELF_SERVICE_STATS_REQUEST_TYPES.COUNT_BY_STATUS]: detailsStats },
    extrasData,
    proactiveWorksData,
    selectedTab
  ) => {
    if (isDetailsLocation) {
      switch (selectedTab) {
        case SELF_SERVICE_STATUS:
          return detailsStats.length > 0;
        case SELF_SERVICE_EXTRAS:
          return extrasData.length > 0;
        case SELF_SERVICE_PROACTIVE_WORKS:
          return proactiveWorksData.length > 0;
        default:
          return false;
      }
    }

    return dashboardStats.length > 0;
  }
);

export const canGenerateExportFile = createSelector(
  getCurrentUserIsManufacturer,
  isSelfServiceStats,
  getDealersStats,
  hasSelfServiceDataToExportSelector,
  (isManufacturer, isSelfServiceStatsLocation, dealerStats, hasSelfServiceDataToExport) =>
    isManufacturer || (isSelfServiceStatsLocation ? hasSelfServiceDataToExport : dealerStats.length > 0)
);

export const getExtraFilters = createSelector(getSelfServiceExtrasReducer, ({ filters }) => filters);
export const getProactiveWorksFilters = createSelector(getSelfServiceProactiveWorksReducer, ({ filters }) => filters);

export const getExtraDetailsParams = createSelector(
  getExtraFilters,
  getSelfServiceStatsDashboardFetchParams,
  (extraFilters, { dateField: _, ...filters }) => ({
    ...extraFilters,
    ...filters
  })
);

export const getProactiveWorksDetailsParams = createSelector(
  getProactiveWorksFilters,
  getSelfServiceStatsDashboardFetchParams,
  (proactiveWorksFilters, { dateField: _, ...filters }) => ({
    ...proactiveWorksFilters,
    ...filters
  })
);

export const getSelfServiceTopContributorsParams = createSelector(
  getSelfServiceTopContributorsFilters,
  getSelfServiceStatsDashboardFetchParams,
  getSelfServiceTopContributorsStatsPage,
  (topContributorsFilters, { dateField: _, ...filters }, page) => ({
    ...filters,
    sort: [topContributorsFilters.sort],
    size: page.size
  })
);

export const getExtraDetailsIsFetching = createSelector(getSelfServiceExtrasReducer, ({ isFetching }) => isFetching);

export const getExtraDetailsApiError = createSelector(getSelfServiceExtrasReducer, ({ apiError }) => apiError);

export const getExtraDetailsPage = createSelector(getSelfServiceExtrasReducer, ({ page }) => page);

export const getProactiveWorksDetailsIsFetching = createSelector(getSelfServiceProactiveWorksReducer, ({ isFetching }) => isFetching);

export const getProactiveWorksDetailsApiError = createSelector(getSelfServiceProactiveWorksReducer, ({ apiError }) => apiError);

export const getProactiveWorksDetailsPage = createSelector(getSelfServiceProactiveWorksReducer, ({ page }) => page);

const getQuestionsFilters = createSelector(
  getSelectedQuestionModelId,
  getFiltersRequest,
  (selectedQuestionModelId, filters) => ({
    questionFormModelId: selectedQuestionModelId,
    response: filters
  })
);

export const getExportParams = createSelector(
  getPeriod,
  getBaseRequestParams,
  getSelectedDealerId,
  getSelectedDealerGroupId,
  getDealersStatsFilters,
  getModules,
  getLocale,
  getSelfServiceTab,
  getExtraFilters,
  getProactiveWorksFilters,
  getQuestionsFilters,
  (
    { from, to },
    { sentFrom: _sentFrom, sentTo: _sentTo, mode: _omitted, dealerId: _, groupId: __, brands, ...params },
    dealerId,
    groupId,
    { countryCodes, dateField },
    modules,
    language,
    selfServiceTab,
    { originChecklistIds: extrasChecklistIds },
    { originChecklistIds: proactiveWorksChecklistIds },
    questionsFilters
  ) => {
    const sort = REQUEST_DATE_FORMATTER[dateField]?.sort ?? REQUEST_DATE_FORMATTER.DEFAULT.sort;

    return {
      ...params,
      brandIds: brands,
      language,
      ...(groupId ? { groupId } : {}),
      ...(dealerId ? { dealerId } : {}),
      modules: modules.split('~').filter(Boolean),
      countryCodes,
      from,
      to,
      sort,
      ...(selfServiceTab === SELF_SERVICE_EXTRAS ? { originChecklistIds: extrasChecklistIds } : {}),
      ...(selfServiceTab === SELF_SERVICE_PROACTIVE_WORKS ? { originChecklistIds: proactiveWorksChecklistIds } : {}),
      ...(selfServiceTab === SELF_SERVICE_QUESTIONS ? questionsFilters : {})
    };
  }
);

export const getNbMobilityContractSigned = createSelector(
  getSelfServiceDetailsDataReducer,
  ({ [SELF_SERVICE_STATS_REQUEST_TYPES.MOBILITY_STATUS]: data }) => data?.[0]?.contractSigned ?? 0
);

export const getMobilityCourtesyVehicleStatus = createSelector(
  getSelfServiceDetailsDataReducer,
  ({ [SELF_SERVICE_STATS_REQUEST_TYPES.MOBILITY_STATUS]: data }) => {
    const { id, accepted, refused, sentOn } = data?.[0] ?? {};
    return [
      [TO_BE_SENT, id],
      [SENT, sentOn],
      [CONFIRMED, accepted],
      [CANCELED, refused]
    ];
  }
);

export const getAllMobilityDocumentsValidationStatus = createSelector(
  getSelfServiceDetailsDataReducer,
  ({ [SELF_SERVICE_STATS_REQUEST_TYPES.MOBILITY_STATUS]: data }) => {
    const {
      manualDocumentsProvided,
      manualDocumentsAccepted,
      manualDocumentsRefused,
      manualDocumentsToValidate,
      automaticDocumentsProvided,
      automaticDocumentsManuallyAccepted,
      automaticDocumentsManuallyRefused,
      automaticDocumentsToValidate,
      automaticDocumentsAutomaticallyAccepted
    } = data?.[0] ?? {};

    return {
      manual: [
        [PROVIDED, manualDocumentsProvided],
        [ACCEPTED, manualDocumentsAccepted],
        [REFUSED, manualDocumentsRefused],
        [TO_VALIDATE, manualDocumentsToValidate]
      ],
      automated: [
        [AUTOMATIC_VALIDATION_PROVIDED, automaticDocumentsProvided],
        [AUTOMATIC_AUTOMATICALLY_ACCEPTED, automaticDocumentsAutomaticallyAccepted],
        [AUTOMATIC_MANUALLY_ACCEPTED, automaticDocumentsManuallyAccepted],
        [MANUALLY_REFUSED, automaticDocumentsManuallyRefused],
        [TO_VALIDATE, automaticDocumentsToValidate],
      ]
    };
  }
);
